import { NextImage } from '@zurich-framework/nextapp/components';
import { useState } from 'react';
import { useTranslation, useValidationError } from '@zurich/common/hooks';
import { appsVSGADataNames } from '@zurich/test/testdatanames';
import { Stack, Typography } from '@zurich/ui/atoms';
import { styled } from '@zurich/ui/legacy/utils';
import { CameraButton } from '@zurich/ui/molecules';
import { DocumentDamageDrawer } from '~/components/Steps/DocumentDamageDrawer';
import { usePhotoUpload } from '~/hooks';
import { isAutoArea, type StepNames } from '~/lib/types';
import { imageValidationSelectorFamily } from '~/state';
import { ButtonBar } from './ButtonBar';

type PhotoUploadWithDamageOptionProps = {
  id: string;
  text: string;
  documentDamage?: boolean;
  stepName: StepNames;
};

export function PhotoUploadWithDamageOption({
  id,
  text,
  documentDamage = false,
  stepName,
}: PhotoUploadWithDamageOptionProps) {
  const { t } = useTranslation();

  const { blobURL, loading, error, handleImageBlob, resetImageBlob } =
    usePhotoUpload(id, stepName);
  const { stepValidationActive, validationError } = useValidationError(
    imageValidationSelectorFamily(stepName)
  );

  const [open, setOpen] = useState(false);
  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  return (
    <>
      <Stack alignItems="center" justifyContent="center">
        <StyledStack
          rowGap={3}
          columnGap={3}
          alignItems="center"
          flexDirection="column"
        >
          {!blobURL && (
            <NextImageStyled
              src="/images/image_placeholder.svg"
              alt="image placeholder"
              width={310}
              height={240}
            />
          )}
          <CameraButton
            dataName={appsVSGADataNames.damageDocumentation.stepPhotoInput}
            handleImageBlob={(file) => void handleImageBlob(file)}
            resetImageBlob={resetImageBlob}
            imageURL={blobURL}
            loading={loading}
            text={text}
            loadingTitle={t('photoLoadingTitle')}
            capture="environment"
            errorMessage={t('vsga:general.unsupportedFileType', {
              datatype: 'ein Bild',
            })}
            variant="outlined"
          />
          {error && (
            <Typography variant="body1 regular" color="error">
              {t(`vsga:errors.${error}`)}
            </Typography>
          )}
          {stepValidationActive && validationError && (
            <Typography variant="body1 regular" color="error">
              {t(`vsga:general.photoUploadValidation.${validationError}`)}
            </Typography>
          )}
          {blobURL && documentDamage && isAutoArea(stepName) && (
            <StyledStackButtonBar>
              <Typography // TODO mobile-friendly Typography in ui/atoms
                variant="body2 regular"
                color="darkBlue"
              >
                {t('vsga:general.damageQuestion')}
              </Typography>
              <ButtonBar
                area={stepName}
                icon="camera-outline"
                text={t('vsga:general.drawerDamagePhotos')}
                onClick={toggleDrawer(true)}
              />
            </StyledStackButtonBar>
          )}
        </StyledStack>
      </Stack>
      <StyledStack>
        <DocumentDamageDrawer
          toggleDrawer={toggleDrawer}
          open={open}
          stepName={stepName}
        />
      </StyledStack>
    </>
  );
}

const StyledStack = styled(Stack)(() => ({
  maxWidth: '340px',
  width: '100%',
}));

const StyledStackButtonBar = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(0, 0),
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 0),
    gap: theme.spacing(2),
  },
}));

const NextImageStyled = styled(NextImage)(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
}));
